import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import {
   Box,
   Button,
   Card,
   colors,
   EmptyState,
   Flex,
   spacing,
   Typography,
   useI18nContext,
} from "@procore/core-react";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import type { CategoryMoveUpdate } from "./utils";
import { CategoryRow } from "./category-row";
import { InlineInput } from "./inline-input";
import type { ActiveRow, FilteredProjectCategoryNode, ProjectCategory } from "./types";
import {
   BORDER,
   CATEGORIES_TRANSLATIONS,
   DropZoneIntent,
   filterCategories,
   flattenCategoryTree,
   flatToTree,
   gripWidth,
} from "./utils";
import { useCategoryDrag } from "./useCategoryDrag";

type CategoryListProps = {
   categories: ProjectCategory[];
   activeRow: ActiveRow | null;
   setActiveRow: (row: ActiveRow | null) => void;
   showSubcategories: boolean;
   search: string;
   onCreate: (params: { name: string; parent_id: string | null }) => void;
   onRename: (params: { id: string; name: string }) => void;
   onDelete: (params: { id: string }) => void;
   onMove: (update: CategoryMoveUpdate) => void;
   isDeleting: boolean;
   isCreating: boolean;
   isRenaming: boolean;
   isMoving: boolean;
};

const { BEFORE, ON } = DropZoneIntent;
const rowHeight = 61;
const dropEdgeLineColor = colors.blue50;

export const CategoryList = ({
   categories,
   activeRow,
   setActiveRow,
   showSubcategories,
   search,
   onCreate,
   onRename,
   onDelete,
   onMove,
   isDeleting,
   isCreating,
   isRenaming,
   isMoving,
}: CategoryListProps) => {
   const I18n = useI18nContext();
   const [newRootName, setNewRootName] = useState("");

   const isAddRootInputOpen = activeRow?.mode === "create" && activeRow.parentId === null;

   useEffect(() => {
      if (isAddRootInputOpen) setNewRootName("");
   }, [isAddRootInputOpen]);

   const rootNodes = useMemo(() => flatToTree(categories), [categories]);

   const flatRows = useMemo(
      () => flattenCategoryTree(rootNodes, showSubcategories),
      [rootNodes, showSubcategories],
   );

   const handleRootSave = () => {
      if (newRootName.trim()) onCreate({ name: newRootName.trim(), parent_id: null });
   };

   const searchResults: FilteredProjectCategoryNode[] | null = search
      ? filterCategories(rootNodes, search)
      : null;

   const {
      dragEnabled,
      dragSource,
      dropTarget,
      afterIndicatorPosition,
      handleDragStart,
      handleDragMove,
      handleDragEnd,
   } = useCategoryDrag({
      categories,
      flatRows,
      activeRow,
      isMoving,
      isSearching: searchResults !== null,
      onMove,
   });

   if (categories.length === 0 && !isAddRootInputOpen) {
      return (
         <EmptyState compact>
            <EmptyState.Title>
               {I18n.t(`${CATEGORIES_TRANSLATIONS}.empty_state_title`)}
            </EmptyState.Title>
            <EmptyState.Description>
               {I18n.t(`${CATEGORIES_TRANSLATIONS}.empty_state_description`)}
            </EmptyState.Description>
            <EmptyState.Actions style={{ marginBottom: spacing.md }}>
               <Button
                  variant="secondary"
                  onClick={() => setActiveRow({ id: null, mode: "create", parentId: null })}
               >
                  {I18n.t(`${CATEGORIES_TRANSLATIONS}.add_category`)}
               </Button>
            </EmptyState.Actions>
         </EmptyState>
      );
   }

   const listBody =
      searchResults?.length === 0 ? (
         <Flex
            alignItems="center"
            paddingLeft="xl"
            paddingTop="md"
            paddingRight="xl"
            paddingBottom="md"
         >
            <Typography color="gray45">
               {I18n.t(`${CATEGORIES_TRANSLATIONS}.search_no_results`)}
            </Typography>
         </Flex>
      ) : searchResults ? (
         searchResults.map((result) => (
            <CategoryRow
               key={result.id}
               node={result}
               index={0}
               matched={result.matched}
               showSubcategories={showSubcategories}
               activeRow={activeRow}
               setActiveRow={setActiveRow}
               onCreate={onCreate}
               onRename={onRename}
               onDelete={onDelete}
               isDeleting={isDeleting}
               isCreating={isCreating}
               isRenaming={isRenaming}
               dragEnabled={false}
               dropIntent={null}
            />
         ))
      ) : (
         flatRows.map((entry, i) => (
            <Fragment key={entry.id}>
               {dragEnabled && dropTarget?.intent === BEFORE && dropTarget.id === entry.id && (
                  <Box
                     style={{
                        height: 0,
                        marginLeft: gripWidth(dropTarget.depth),
                        outline: `1px solid ${dropEdgeLineColor}`,
                     }}
                  />
               )}
               <CategoryRow
                  node={entry}
                  index={i}
                  matched={true}
                  showSubcategories={showSubcategories}
                  activeRow={activeRow}
                  setActiveRow={setActiveRow}
                  onCreate={onCreate}
                  onRename={onRename}
                  onDelete={onDelete}
                  isDeleting={isDeleting}
                  isCreating={isCreating}
                  isRenaming={isRenaming}
                  dragEnabled={dragEnabled}
                  dropIntent={
                     dragEnabled && dropTarget?.intent === ON && dropTarget.id === entry.id
                        ? ON
                        : null
                  }
               />
               {afterIndicatorPosition?.index === i && (
                  <div
                     style={{
                        height: 0,
                        marginLeft: gripWidth(afterIndicatorPosition.depth),
                        outline: `1px solid ${dropEdgeLineColor}`,
                     }}
                  />
               )}
            </Fragment>
         ))
      );

   const scrollBox = (
      <Box
         style={{
            border: BORDER,
            borderRadius: 8,
            overflowY: "auto",
            maxHeight: rowHeight * 9.5,
         }}
      >
         {listBody}
         {!searchResults && isAddRootInputOpen && (
            <Flex paddingLeft="xl" paddingTop="md" paddingRight="xl" paddingBottom="md">
               <InlineInput
                  value={newRootName}
                  onChange={setNewRootName}
                  onSave={handleRootSave}
                  onCancel={() => setActiveRow(null)}
                  placeholder={I18n.t(`${CATEGORIES_TRANSLATIONS}.new_category_placeholder`)}
                  ariaLabel={I18n.t(`${CATEGORIES_TRANSLATIONS}.aria_new_category`)}
                  isSaving={isCreating}
               />
            </Flex>
         )}
      </Box>
   );

   return (
      <DragDropProvider
         onDragStart={dragEnabled ? handleDragStart : undefined}
         onDragMove={dragEnabled ? handleDragMove : undefined}
         onDragEnd={dragEnabled ? handleDragEnd : undefined}
      >
         {scrollBox}
         {dragEnabled && (
            <DragOverlay>
               {dragSource ? (
                  <Card style={{ padding: spacing.sm, opacity: 0.7 }}>
                     <Typography weight="semibold">{dragSource.name}</Typography>
                  </Card>
               ) : null}
            </DragOverlay>
         )}
      </DragDropProvider>
   );
};
